import { MOVE_ROW_PROCESS_STATUS } from '@/modules/move/entities/enums/move';
import { MOVE_ERROR_TYPE, MoveSheetNormalized } from '@/modules/move/entities/interfaces/moveSheet';
import { EditedRow } from '@/modules/move/hooks/useMoveEdition/useMoveEdition';
import { useClipBoard } from '@/shared/hooks/useClipBoard';
import ICONS from '@/shared/entities/constants/icons';
import React, { useMemo } from 'react';
import { Button, Chip, Text } from 'vkit/lib/components';
import { Grid, Tooltip } from 'vkit/lib/context';
import ChipType from 'vkit/lib/shared/components/chip/types/chip.type';

interface ReportListItemHeaderProps {
  moveSheetNormalized: MoveSheetNormalized;
  setRowToEdition?: (move: MoveSheetNormalized) => void;
  editedRow?: EditedRow;
}

export const ReportListItemHeader: React.FC<ReportListItemHeaderProps> = ({
  moveSheetNormalized,
  setRowToEdition,
  editedRow,
}) => {
  const { isCopied, copyToClipboard } = useClipBoard();
  const hasReviewError =
    moveSheetNormalized.hasError && moveSheetNormalized.errorType === MOVE_ERROR_TYPE.REVIEW_ERROR;
  const hasValidationError =
    moveSheetNormalized.hasError && moveSheetNormalized.errorType !== MOVE_ERROR_TYPE.REVIEW_ERROR;
  const statusConfig = useMemo((): {
    color: ChipType['color'];
    label: string;
    title: string;
  } | null => {
    if (
      (editedRow &&
        editedRow.fields.statusBeneficiario === MOVE_ROW_PROCESS_STATUS.WAITING_HEALTH_CARD) ||
      (!editedRow &&
        moveSheetNormalized.originalContent.statusBeneficiario ===
          MOVE_ROW_PROCESS_STATUS.WAITING_HEALTH_CARD)
    ) {
      return {
        color: 'info',
        label: 'Vida ativa',
        title: 'Vida já esta ativa na operadora, portanto, não há necessidade de edição.',
      };
    }

    if (
      editedRow &&
      editedRow.fields.statusBeneficiario !== MOVE_ROW_PROCESS_STATUS.WAITING_HEALTH_CARD
    ) {
      return {
        color: 'success',
        label: 'Editado',
        title:
          'Informações da vida foram editadas, mas ainda não foram salvas, clique em "Salvar alterações" para concluir a edição.',
      };
    }

    return null;
  }, [editedRow, moveSheetNormalized.originalContent.statusBeneficiario]);

  return (
    <Grid alignContent='justify'>
      <Grid gap={24} alignItems='center'>
        <>
          <Grid gap={8}>
            <Text value='Linha:' size='medium' />
            <Text fontWeight='bold' value={moveSheetNormalized.line.toString()} size='medium' />
          </Grid>
          {hasValidationError && (
            <Grid gap={8}>
              <Chip size='small' rounded color='danger' label='Contém erro(s)' />
            </Grid>
          )}
          {hasReviewError && (
            <Grid gap={8}>
              <Chip size='small' rounded color='warning' label='Contém crítica(s)' />
            </Grid>
          )}
          {moveSheetNormalized.originalContent.carteirinha && (
            <Grid gap={8}>
              <Tooltip title={isCopied ? 'Copiado!' : 'Copiar'}>
                <Button
                  size='small'
                  rounded
                  color='success'
                  invertColor
                  label={`Carteirinha: ${moveSheetNormalized.originalContent.carteirinha}`}
                  onClick={async (event) => {
                    event?.stopPropagation();
                    await copyToClipboard(String(moveSheetNormalized.originalContent.carteirinha));
                  }}
                />
              </Tooltip>
            </Grid>
          )}
        </>
      </Grid>

      {moveSheetNormalized.hasError && (
        <Grid gap={16} margin='0 16px 0'>
          {statusConfig && (
            <Tooltip title={statusConfig.title}>
              <Chip size='small' rounded color={statusConfig.color} label={statusConfig.label} />
            </Tooltip>
          )}

          <Button
            onClick={(event) => {
              event?.stopPropagation();
              setRowToEdition?.(moveSheetNormalized);
            }}
            label='Editar'
            icon={ICONS.EDIT}
            color='colorDefault'
            size='small'
          />
        </Grid>
      )}
    </Grid>
  );
};
